<template>
  <div class="product-card">
    <div class="product-image">
      <button 
        class="favorite-btn" 
        @click.stop="toggleFavorito"
        :class="{ 'favorited': isFavorito }"
        :title="isFavorito ? 'Remover dos favoritos' : 'Adicionar aos favoritos'"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path 
            d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" 
            :fill="isFavorito ? 'currentColor' : 'none'"
            stroke="currentColor" 
            stroke-width="2" 
            stroke-linecap="round" 
            stroke-linejoin="round"
          />
        </svg>
      </button>
      <img 
        v-if="imagemProduto && !imageError" 
        :src="imagemProduto" 
        :alt="produto.nome || produto.descricao"
        @error="onImageError"
      />
      <img 
        v-else-if="imagemPadrao" 
        :src="imagemPadrao" 
        :alt="produto.nome || produto.descricao"
      />
      <div v-else class="image-placeholder">
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 16L8.586 11.414C9.367 10.633 10.633 10.633 11.414 11.414L16 16M14 14L15.586 12.414C16.367 11.633 17.633 11.633 18.414 12.414L20 14M14 8H14.01M6 20H18C19.105 20 20 19.105 20 18V6C20 4.895 19.105 4 18 4H6C4.895 4 4 4.895 4 6V18C4 19.105 4.895 20 6 20Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </div>
    <div class="product-info">
      <h3 class="product-title">{{ produto.nome }}</h3>
      <p v-if="produto.nome && produto.descricao" class="product-description">{{ descricaoTruncada }}</p>
      <div class="product-footer">
        <span class="product-price">R$ {{ formatPrice(produto.valor) }}</span>
        <router-link 
          v-if="produto.qtdEstoque > 0"
          :to="`/produto/${produto.id}`" 
          class="btn-details"
        >
          Ver Detalhes
        </router-link>
        <button 
          v-else
          disabled
          class="btn-details btn-details-disabled"
          title="Produto sem estoque"
        >
          Sem Estoque
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import favoritosStore from '../store/favoritos'

export default {
  name: 'ProductCard',
  props: {
    produto: {
      type: Object,
      required: true
    },
    categorias: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      imageError: false,
      favoritado: false
    }
  },
  computed: {
    imagemProduto() {
      const img = this.produto.imagem

      // Se houver erro ao carregar a imagem ou não existir imagem, retornar null
      if (this.imageError || !img) {
        return null
      }

      // Se a imagem já é uma URL completa (http/https), retornar como está
      if (img.startsWith('http://') || img.startsWith('https://')) {
        return img
      }

      // Se vier como nome de arquivo (ex: "vaso_teste_20260312191823201.jpg"),
      // montar URL do servidor de imagens
      if (typeof img === 'string' && img.length < 200 && img.indexOf('.') !== -1) {
        return `https://api.grupoparque.com.br/produtos/${img}`
      }

      // Se a imagem é base64 com prefixo data URI
      if (img.startsWith('data:')) {
        return img
      }

      // Se é base64 sem prefixo, adicionar o prefixo
      return `data:image/jpeg;base64,${img}`
    },
    imagemPadrao() {
      const categoria = this.categorias.find(c => c.id === this.produto.categoriaId)
      const nomeCategoria = categoria ? categoria.descricao.toLowerCase() : ''
      const nomeProduto = (this.produto.nome || this.produto.descricao || '').toLowerCase()
      
      // Mapeamento de categorias para imagens padrão
      const mapeamentoImagens = {
        'coroa': 'coroa.jpg',
        'coroas': 'coroa.jpg',
        'urna': 'urna.jpg',
        'urnas': 'urna.jpg',
        'cremação': 'cremacao.jpg',
        'cremacao': 'cremacao.jpg',
        'cremações': 'cremacao.jpg',
        'jazigo': 'jazigo.jpg',
        'jazigos': 'jazigo.jpg',
        'plano funerário': 'plano.jpg',
        'planos funerários': 'plano.jpg',
        'planos funerarios': 'plano.jpg',
        'buque': 'buque.jpg',
        'buquê': 'buque.jpg',
        'buques': 'buque.jpg',
        'buquês': 'buque.jpg',
        'vaso': 'vaso.jpg',
        'vasos': 'vaso.jpg',
        'arranjo': 'arranjo.jpg',
        'arranjos': 'arranjo.jpg',
        'arranjo floral': 'arranjo.jpg',
        'arranjos florais': 'arranjo.jpg'
      }
      
      // Buscar imagem padrão baseada no nome da categoria primeiro
      for (const [key, imagem] of Object.entries(mapeamentoImagens)) {
        if (nomeCategoria.includes(key)) {
          return `/img/${imagem}`
        }
      }
      
      // Se não encontrar na categoria, buscar no nome do produto
      for (const [key, imagem] of Object.entries(mapeamentoImagens)) {
        if (nomeProduto.includes(key)) {
          return `/img/${imagem}`
        }
      }
      
      return null
    },
    descricaoTruncada() {
      const desc = this.produto.descricao
      if (!desc) return ''
      const maxLength = 90
      if (desc.length <= maxLength) return desc
      return desc.slice(0, maxLength).trim() + '...'
    },
    isFavorito() {
      return this.favoritado
    }
  },
  mounted() {
    this.favoritado = favoritosStore.isFavorito(this.produto.id)
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },
    onImageError() {
      this.imageError = true
    },
    toggleFavorito() {
      favoritosStore.toggleFavorito(this.produto.id)
      this.favoritado = favoritosStore.isFavorito(this.produto.id)
    }
  },
}
</script>

<style scoped>
.product-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.product-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.product-image {
  width: 100%;
  height: 200px;
  background: linear-gradient(135deg, #48bb78 0%, #34894d 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  color: white;
  opacity: 0.7;
}

.product-info {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.product-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 6px;
  line-height: 1.4;
}

.product-description {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
  margin: 0 0 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.product-type {
  font-size: 14px;
  color: #718096;
  margin-bottom: 12px;
}

.product-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  flex: 1;
}

.product-supplier {
  font-size: 13px;
  color: #4a5568;
}

.product-stock {
  font-size: 13px;
  color: #48bb78;
  font-weight: 500;
}

.product-stock.low-stock {
  color: #f56565;
}

.product-stock.out-of-stock {
  color: #f56565;
  font-weight: 600;
}

.product-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.product-price {
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
}

.btn-details {
  background: #34894d;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-decoration: none;
  display: inline-block;
  text-align: center;
}

.btn-details:hover {
  background: #2d7a42;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(52, 137, 77, 0.2);
}

.btn-details:active {
  transform: translateY(0);
}

.btn-details-disabled {
  background: #9ca3af !important;
  color: white !important;
  cursor: not-allowed !important;
  opacity: 0.6;
}

.btn-details-disabled:hover {
  background: #9ca3af !important;
  transform: none !important;
  box-shadow: none !important;
}

.favorite-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  z-index: 10;
  color: #6b7280;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.favorite-btn:hover {
  background: rgba(255, 255, 255, 1);
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.favorite-btn.favorited {
  color: #ef4444;
  background: rgba(255, 255, 255, 1);
}

.favorite-btn svg {
  width: 20px;
  height: 20px;
}
</style>

